import { _ } from "../../../../core/polyglot/polyglot.js";
import { formatFullWithTwoDecimals } from "../../../../core/helpers/currency-formatting-util.js";
import { router, Widget } from "@foretagsplatsen/widgetjs";
import FormulaRendererVisitor from "../../../../models/keyFigure/formula/visitors/FormulaRendererVisitor.js";
import keyFigureValueFormatter from "../../../../models/keyFigure/rendering/keyFigureValueFormatter.js";

/**
 * Displays a table with the evolution of a `KeyFigure`'s values
 * across periods. Each row represents a different period.
 *
 * The first column shows the period name. The last column shows the
 * value of the key figure for the period.  Middle columns show values
 * for "interesting parts" of the key figure formula. The "interesting
 * parts" must be configured using `setup()`.
 *
 * @param {Period} selectedPeriod - The period to highlight in the table
 * @param {Dimension} [dimensionTree] - Root dimension from which
 *   dimension ids are resolved
 */
export default class KeyFigureComponentsWidget extends Widget {
	constructor({ selectedPeriod, dimensionTree }) {
		super(...arguments);

		this._selectedPeriod = selectedPeriod;
		this._dimensionTree = dimensionTree;

		this._keyFigureResult = null;

		// Map with formula nodes as keys and period maps as values. A
		// period map has a period as key and a KeyFigureValue as
		// value.
		this._interestingNodeValues = null;

		this._formatter = keyFigureValueFormatter({
			currencyFormatter: formatFullWithTwoDecimals,
		});
	}

	/**
	 * Initialize the widget with the values to display.
	 *
	 * @param {KeyFigureResult} keyFigureResult - The values to
	 *   display in the last column (the one corresponding to the KPI
	 *   being displayed)
	 * @param {Map<AbstractFormulaNode, Map<Period, KeyFigureValue>>} [interestingNodeValues] - The values for the middle columns containing the interesting nodes. These "interesting"
	 *  nodes can, for example, be generated with {@link InterestingNodesFinderFormulaVisitor}
	 */
	setup({ keyFigureResult, interestingNodeValues = new Map() }) {
		this._keyFigureResult = keyFigureResult;
		this._interestingNodeValues = interestingNodeValues;
	}

	renderContentOn(html) {
		if (this._keyFigureResult) {
			this._renderNonEmptyOn(html);
		} else {
			this._renderEmptyOn(html);
		}
	}

	_getKeyFigure() {
		return this._keyFigureResult.getFirstKeyFigure();
	}

	_renderEmptyOn() {}

	_renderNonEmptyOn(html) {
		html.div(
			{ klass: "table-responsive" },
			html.table(
				{ klass: "table table-hover table-striped table-bordered" },
				this._renderTheadOn.bind(this),
				this._renderTbodyOn.bind(this),
			),
		);
	}

	_renderTheadOn(html) {
		html.thead(
			html.tr(
				{ "data-test": "component-names" },
				html.th(_("Period")),
				this._getInterestingNodes().map((interestingNode) =>
					html.th(this._interestingNodeRenderer(interestingNode)),
				),
				html.th(this._getKeyFigure().getName() || _("n/a")),
			),
		);
	}

	_renderTbodyOn(html) {
		html.tbody(
			this._keyFigureResult
				.getValues()
				.reverse()
				.map((value) =>
					html.tr(
						{
							klass: this._tableDataClassForValue(value),
							"data-test": "component-values",
						},
						html.td(value.getPeriod().toSmartString()),

						this._interestingNodeValuesForPeriod(
							value.getPeriod(),
						).map((interestingNodeValue) =>
							this._valueRenderer(interestingNodeValue),
						),

						// KeyFigure value
						this._valueRenderer(value),
					),
				),
		);
	}

	_tableDataClassForValue(value) {
		return { "current-period": value.getPeriod() === this._selectedPeriod };
	}

	_interestingNodeRenderer(formulaNode) {
		return formulaNode.accept(
			new FormulaRendererVisitor({
				dimensionTree: this._dimensionTree,
			}),
		);
	}

	_valueRenderer(value) {
		return (html) => {
			if (!value || !value.getValue()) {
				html.td(html.span("-"));
				return;
			}

			const formattedValue = this._formatter.format(value) || "-";
			const href = this._getLinkToKeyFigure(value);

			const cellContent = href
				? (html) => html.a({ href }, formattedValue)
				: formattedValue;

			html.td(cellContent);
		};
	}

	_getLinkToKeyFigure(value) {
		return value.getComponent() === this._getKeyFigure()
			? this._getLinkToCurrentKeyFigure(value)
			: this._getLinkToOtherKeyFigure(value);
	}

	_getLinkToCurrentKeyFigure(value) {
		if (value.getPeriod() === this._selectedPeriod) {
			// A link in this case would point to the same page
			return undefined;
		}

		return this._linkToComponent({
			keyFigure: value.getComponent(),
			period: value.getPeriod(),
		});
	}

	_getLinkToOtherKeyFigure(value) {
		const formulaNode = value.getComponent().getFormula();

		if (!formulaNode.isKeyFigureFormulaNode()) {
			return undefined;
		}

		return this._linkToComponent({
			keyFigure: formulaNode.getKeyFigure(),
			period: value.getPeriod(),
		});
	}

	_linkToComponent({ keyFigure, period }) {
		return router.getRouter().linkToParameters({
			keyFigure: keyFigure.getId(),
			period: period.toString(),
		});
	}

	_interestingNodeValuesForPeriod(period) {
		return this._getInterestingNodes().map((interestingNode) =>
			this._interestingNodeValues.get(interestingNode).get(period),
		);
	}

	_getInterestingNodes() {
		return [...this._interestingNodeValues.keys()];
	}
}
